/**
 * Owns the live-canvas preview player for the editor.
 *
 * videoStore decides WHEN (play / pause / scrub); this hook renders the
 * frame. Any interaction outside the timeline ends preview first, so edits
 * always land on the restored, authored canvas and are never swallowed by
 * the history suppression that preview needs.
 */

import { useEffect, useRef } from 'react';
import type { fabric } from 'fabric';
import { createPreviewPlayer, type PreviewPlayer } from '@/lib/video/previewPlayer';
import { useVideoStore } from '@/stores/videoStore';

/** Clicks inside these keep preview running (play button, scrub ruler, …). */
const PREVIEW_SAFE_SELECTOR = '.vt-timeline, .vt-anim-preview';

interface UseVideoPreviewOptions {
  canvas: fabric.Canvas | null;
  ready: boolean;
  setHistorySuppressed: (on: boolean) => void;
  /** False while the editor is loading a page into Fabric. */
  canEnter: () => boolean;
  artboard: () => { width: number; height: number };
}

export function useVideoPreview({
  canvas,
  ready,
  setHistorySuppressed,
  canEnter,
  artboard,
}: UseVideoPreviewOptions): void {
  const depsRef = useRef({ setHistorySuppressed, canEnter, artboard });
  depsRef.current = { setHistorySuppressed, canEnter, artboard };

  useEffect(() => {
    if (!canvas || !ready) return;

    const player: PreviewPlayer = createPreviewPlayer({
      canvas,
      setHistorySuppressed: (on) => depsRef.current.setHistorySuppressed(on),
      canEnter: () => depsRef.current.canEnter(),
      artboard: () => depsRef.current.artboard(),
      onError: (err) => {
        console.error('[preview] frame failed; canvas restored', err);
        const store = useVideoStore.getState();
        store.stop();
        store.setNotice('Preview stopped because an animation failed. Your design is unchanged.');
      },
    });

    const sync = (): void => {
      const { playback, previewLocalMs, previewCue } = useVideoStore.getState();
      if (playback !== 'stopped' && previewLocalMs !== null && previewCue) {
        player.renderAt(previewLocalMs, previewCue.endMs - previewCue.startMs);
      } else {
        player.exit();
      }
    };
    const unsubscribe = useVideoStore.subscribe(sync);
    sync();

    const stopIfActive = (): void => {
      if (player.isActive()) useVideoStore.getState().stop();
    };
    // Capture phase: runs before Fabric's own pointer handlers and the editor's shortcuts.
    const onPointerDown = (e: PointerEvent): void => {
      const target = e.target as Element | null;
      if (target?.closest?.(PREVIEW_SAFE_SELECTOR)) return;
      stopIfActive();
    };
    const onKeyDown = (e: KeyboardEvent): void => {
      // Space is play/pause; everything else (Delete, Ctrl+Z, Ctrl+E, …) edits or exports.
      if (e.code === 'Space') return;
      stopIfActive();
    };
    window.addEventListener('pointerdown', onPointerDown, true);
    window.addEventListener('keydown', onKeyDown, true);
    window.addEventListener('pagehide', stopIfActive);

    return () => {
      unsubscribe();
      window.removeEventListener('pointerdown', onPointerDown, true);
      window.removeEventListener('keydown', onKeyDown, true);
      window.removeEventListener('pagehide', stopIfActive);
      useVideoStore.getState().stop();
      player.destroy();
    };
  }, [canvas, ready]);
}
